import { API_BASE_URL, authToken } from '../../config';
import ElementTemplate from '../ElementTemplate';
import { CertificateData } from './GiftCertificates';
import { getModalLabels, type ModalLabels } from './giftCertModalLabels';
import { validateContactFields } from './giftCertFormValidation';

type ValidatedField = { input: HTMLInputElement; errorEl: HTMLDivElement };

export default class CertificateModalElement extends ElementTemplate {
  closeBtn!: ElementTemplate<HTMLButtonElement>;

  private form!: HTMLFormElement;

  private labels: ModalLabels;

  private nameField!: ValidatedField;

  private emailField!: ValidatedField;

  private phoneField!: ValidatedField;

  constructor(data: CertificateData) {
    super(null, 'div', 'cert-modal');
    this.labels = getModalLabels();
    this.createModal(data);
  }

  private createModal(data: CertificateData): void {
    new ElementTemplate(this.node, 'div', 'cert-modal__overlay');
    const content = new ElementTemplate(this.node, 'div', 'cert-modal__content');
    new ElementTemplate(content.node, 'h3', 'cert-modal__title', this.labels.title);
    this.createForm(content.node, data);
    this.closeBtn = new ElementTemplate<HTMLButtonElement>(content.node, 'button', 'cert-modal__close');
    this.closeBtn.node.type = 'button';
  }

  private createForm(parent: HTMLElement, data: CertificateData): void {
    this.form = document.createElement('form');
    this.form.className = 'cert-modal__form';
    this.form.setAttribute('novalidate', '');
    parent.append(this.form);

    if (data.type) {
      this.addField(this.labels.certType, 'certificateType', { type: 'text', value: data.type, readonly: true });
    } else {
      this.addField('', 'certificateType', { type: 'hidden', value: 'Individual' });
    }
    if (data.amount === 'choose') {
      this.addSelect(this.labels.amount, 'amount', this.labels.amountModalOptions);
    } else if (data.amount) {
      const v = `${Number(data.amount).toLocaleString('ru-RU')} ${this.labels.currency}`;
      this.addField(this.labels.amount, 'amount', { type: 'text', value: v, readonly: true });
    } else {
      this.addField('', 'amount', { type: 'hidden', value: 'Individual' });
    }

    this.nameField = this.addValidatedField(this.labels.name, 'name', 'text', { autocomplete: 'name' });
    this.emailField = this.addValidatedField(this.labels.email, 'email', 'email', { autocomplete: 'email' });
    this.phoneField = this.addValidatedField(this.labels.phone, 'phone', 'tel', {
      autocomplete: 'tel',
      placeholder: '+381 ...',
    });

    const consent = new ElementTemplate(this.form, 'label', 'cert-modal__consent');
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.name = 'consent';
    cb.required = true;
    cb.className = 'cert-modal__checkbox';
    consent.node.append(cb);
    const span = document.createElement('span');
    span.textContent = this.labels.consent;
    consent.node.append(span);

    const submitBtn = new ElementTemplate<HTMLButtonElement>(this.form, 'button', 'cert-modal__submit btn btn_dark');
    submitBtn.node.type = 'submit';
    submitBtn.node.textContent = this.labels.submit;

    const status = new ElementTemplate(this.form, 'div', 'cert-modal__status');
    status.node.style.display = 'none';
    this.form.addEventListener('submit', (e) => this.handleSubmit(e, status.node));
  }

  private addField(
    label: string,
    name: string,
    opts: { type: string; value?: string; readonly?: boolean; placeholder?: string },
  ): void {
    const group = new ElementTemplate(this.form, 'div', 'cert-modal__field');
    new ElementTemplate(group.node, 'label', 'cert-modal__label', label);
    const input = document.createElement('input');
    input.type = opts.type;
    input.name = name;
    input.className = 'cert-modal__input';
    if (opts.value != null) input.value = opts.value;
    if (opts.readonly) input.readOnly = true;
    if (opts.placeholder) input.placeholder = opts.placeholder;
    input.required = !opts.readonly;
    group.node.append(input);
  }

  private addSelect(label: string, name: string, options: { value: string; label: string }[]): void {
    const group = new ElementTemplate(this.form, 'div', 'cert-modal__field');
    new ElementTemplate(group.node, 'label', 'cert-modal__label', label);
    const select = document.createElement('select');
    select.name = name;
    select.className = 'cert-modal__select';
    select.required = true;
    options.forEach((o) => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      select.append(opt);
    });
    group.node.append(select);
  }

  private addValidatedField(
    label: string,
    name: string,
    type: string,
    extra: { autocomplete?: string; placeholder?: string } = {},
  ): ValidatedField {
    const group = new ElementTemplate(this.form, 'div', 'cert-modal__field');
    new ElementTemplate(group.node, 'label', 'cert-modal__label', label);
    const input = document.createElement('input');
    input.type = type;
    input.name = name;
    input.className = 'cert-modal__input';
    input.required = true;
    if (extra.autocomplete) input.setAttribute('autocomplete', extra.autocomplete);
    if (extra.placeholder) input.placeholder = extra.placeholder;
    const errorEl = document.createElement('div');
    errorEl.className = 'cert-modal__field-error';
    errorEl.hidden = true;
    const clear = () => {
      errorEl.hidden = true;
      input.classList.remove('cert-modal__input_error');
    };
    input.addEventListener('input', clear);
    group.node.append(input);
    group.node.append(errorEl);
    return { input, errorEl };
  }

  private showFieldError(field: ValidatedField, message: string): void {
    field.errorEl.textContent = message;
    field.errorEl.hidden = false;
    field.input.classList.add('cert-modal__input_error');
  }

  private applyContactErrors(errors: ReturnType<typeof validateContactFields>): void {
    if (errors.name) this.showFieldError(this.nameField, errors.name);
    if (errors.email) this.showFieldError(this.emailField, errors.email);
    if (errors.phone) this.showFieldError(this.phoneField, errors.phone);
  }

  private async handleSubmit(e: Event, statusEl: HTMLElement): Promise<void> {
    e.preventDefault();
    statusEl.style.display = 'none';

    if (!this.form.querySelector<HTMLInputElement>('input[name="consent"]')?.checked) {
      statusEl.textContent = this.labels.errConsent;
      statusEl.className = 'cert-modal__status cert-modal__status_error';
      statusEl.style.display = 'block';
      return;
    }

    const errors = validateContactFields(
      {
        name: this.nameField.input.value,
        email: this.emailField.input.value,
        phone: this.phoneField.input.value,
      },
      this.labels,
    );
    if (Object.keys(errors).length) {
      this.applyContactErrors(errors);
      return;
    }

    const submitBtn = this.form.querySelector<HTMLButtonElement>('.cert-modal__submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = this.labels.sending;
    }

    try {
      const body = Object.fromEntries(new FormData(this.form).entries());
      const res = await fetch(`${API_BASE_URL}/certificates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'frontend-auth-token': authToken,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Server error');
      statusEl.textContent = this.labels.success;
      statusEl.className = 'cert-modal__status cert-modal__status_success';
      statusEl.style.display = 'block';
      this.form.reset();
    } catch {
      statusEl.textContent = this.labels.error;
      statusEl.className = 'cert-modal__status cert-modal__status_error';
      statusEl.style.display = 'block';
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = this.labels.submit;
      }
    }
  }
}
